import { useMemo } from 'react';
import { useParams } from 'react-router';

import { type Crumb, type PageIntroHighlight } from '@/components/molecules';
import {
  findService,
  type Service,
  serviceCatalogue,
  servicePath,
  type ServicePillar,
  servicePillars,
} from '@/content/services';

import { serviceCrumbTrail } from '../content';

/** Everything a resolved service page draws, assembled once. */
export type ServiceDetail = {
  service: Service;
  /** The pillar this service belongs to, for the eyebrow and the siblings band. */
  pillar: ServicePillar;
  /** Home → Services → this service, for both the trail and the schema. */
  crumbs: readonly Crumb[];
  /** The trail with Home in front, which only `breadcrumbSchema` needs. */
  schemaCrumbs: readonly Crumb[];
  path: string;
  /**
   * The intro's trust strip, built from the service's own facts.
   *
   * The strip holds three short labels, and a fact is a label *and* a value —
   * so the mapping is a decision rather than a pass-through: the value is
   * what the strip shows, because "30% or 50% of the CIF price" says something on its
   * own and "To start" does not. The two services carrying four facts lose the
   * fourth here and keep all four in the aside, which is the block with room.
   */
  highlights: readonly PageIntroHighlight[];
  /** The other services in the same pillar. Empty for a pillar of one. */
  siblings: readonly Service[];
};

/** The strip has room for three. */
const STRIP_SLOTS = 3;

/**
 * The service the URL names, with everything the page draws derived from it —
 * or `null` when the slug matches nothing.
 *
 * `null` rather than a thrown error or a redirect: a mistyped or retired slug
 * is an ordinary thing for a URL to be, and the page answers it with the same
 * `StatusState` the guide and accessory pages use. The page branches on this
 * once, at the top, and everything below it has a `Service` and not a maybe.
 *
 * Returning the pillar alongside the service is what keeps `servicePillars`
 * out of the bands: the eyebrow wants the pillar's title and the siblings band
 * wants its members, and neither should be re-deriving a lookup the route has
 * already done.
 */
export function useServiceDetail(): ServiceDetail | null {
  const { slug = '' } = useParams();

  return useMemo(() => {
    const service = findService(slug);
    if (!service) return null;

    // Every service's pillar is one of the three by construction — `pillar` is
    // typed as `ServicePillarId` — so this cannot miss. The fallback is for
    // the compiler's benefit rather than the reader's.
    const pillar = servicePillars.find((p) => p.id === service.pillar) ?? servicePillars[0];

    const crumbs: readonly Crumb[] = [...serviceCrumbTrail, { label: service.title }];

    return {
      service,
      pillar,
      crumbs,
      schemaCrumbs: [{ label: 'Home', to: '/' }, ...crumbs],
      path: servicePath(service),
      highlights: service.facts
        .slice(0, STRIP_SLOTS)
        .map<PageIntroHighlight>((fact) => ({ icon: fact.icon, label: fact.value })),
      siblings: serviceCatalogue.filter(
        (other) => other.pillar === service.pillar && other.slug !== service.slug,
      ),
    };
  }, [slug]);
}
